import { type ReactElement, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  isCsvResultsFile,
  isJsonResultsFile,
  isSpreadsheetResultsFile,
  isXmlResultsFile,
} from '@/features/dashboard/sections/resultsFileUploadUtils';
import { useCurrentOrgRole } from '@/shared/hooks';
import { resolveSemanticColor } from '@/theme/semanticTokens';
import { r, s, tableTokens } from '@/theme/tokens';
import { typography } from '@/theme/tokens/typography';
import { Box, InlineError, Link, Stack, Text } from '@/ui';
import {
  Chip,
  CloseIcon,
  HelpOutline,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  MenuItem,
  Tooltip,
  UploadFile,
} from '@/ui/primitives';
import { Checkbox } from '@/ui/primitives/Checkbox';
import { Select } from '@/ui/primitives/Select';
import { TextField } from '@/ui/primitives/TextField';
import type { ProjectDto } from '@flowqa/shared';

type ResultsFileUploadDialogProps = {
  open: boolean;
  loading: boolean;
  confirmDisabled: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedProjectId: string | null;
  uploadProjectId: string;
  uploadTagsInput: string;
  onUploadProjectIdChange: (value: string) => void;
  onUploadTagsInputChange: (value: string) => void;
  uploadProjectIdFieldId: string;
  projectOptions: ProjectDto[];
  uploadFiles: File[];
  selectedFiles: Set<string>;
  isSupportedFile: (file: File) => boolean;
  csvHeaderStatusByName: Record<string, 'pending' | 'valid' | 'invalid' | undefined>;
  csvHeaderReasonByName: Record<string, string | null | undefined>;
  fileValidationStatusByName: Record<string, 'pending' | 'valid' | 'invalid' | undefined>;
  fileValidationReasonByName: Record<string, string | null | undefined>;
  handleFilesSelected: (files: File[]) => void;
  setSelectedFiles: (updater: (current: Set<string>) => Set<string>) => void;
  setUploadFiles: (updater: (current: File[]) => File[]) => void;
  setUploadError: (value: string | null) => void;
  uploadError: string | null;
  setCsvHeaderStatusByName: (
    updater: (
      current: Record<string, 'pending' | 'valid' | 'invalid' | undefined>,
    ) => Record<string, 'pending' | 'valid' | 'invalid' | undefined>,
  ) => void;
  setCsvHeaderReasonByName: (
    updater: (
      current: Record<string, string | null | undefined>,
    ) => Record<string, string | null | undefined>,
  ) => void;
  setFileValidationStatusByName: (
    updater: (
      current: Record<string, 'pending' | 'valid' | 'invalid' | undefined>,
    ) => Record<string, 'pending' | 'valid' | 'invalid' | undefined>,
  ) => void;
  setFileValidationReasonByName: (
    updater: (
      current: Record<string, string | null | undefined>,
    ) => Record<string, string | null | undefined>,
  ) => void;
};

type SpacingKey = 'none' | 'xs' | 'sm' | 'md' | 'lg' | '5xl';

const resolveSpacing = (token: SpacingKey): string | number => {
  if (token === 'none') return 0;
  if (token === '5xl') return s('5xl');
  return s(token);
};

export const ResultsFileUploadDialog = ({
  open,
  loading,
  confirmDisabled,
  onClose,
  onConfirm,
  selectedProjectId,
  uploadProjectId,
  uploadTagsInput,
  onUploadProjectIdChange,
  onUploadTagsInputChange,
  uploadProjectIdFieldId,
  projectOptions,
  uploadFiles,
  selectedFiles,
  isSupportedFile,
  csvHeaderStatusByName,
  csvHeaderReasonByName,
  fileValidationStatusByName,
  fileValidationReasonByName,
  handleFilesSelected,
  setSelectedFiles,
  setUploadFiles,
  setUploadError,
  uploadError,
  setCsvHeaderStatusByName,
  setCsvHeaderReasonByName,
  setFileValidationStatusByName,
  setFileValidationReasonByName,
}: ResultsFileUploadDialogProps): ReactElement => {
  const [isDropActive, setIsDropActive] = useState(false);
  const { session } = useAuth();
  const { isAdmin } = useCurrentOrgRole(session?.userId, { enabled: open });
  const activeProjectId = selectedProjectId ?? uploadProjectId;
  const activeProject = useMemo(
    () => projectOptions.find((project) => project.id === activeProjectId),
    [activeProjectId, projectOptions],
  );
  const isPersonalProject = activeProject?.organisationId === null;
  const isProjectOwner =
    Boolean(activeProject) && isPersonalProject && activeProject.createdById === session?.userId;
  const canManageProject = isAdmin || isProjectOwner;
  const configureCsvHref = useMemo(() => {
    if (!activeProjectId) return null;
    if (!canManageProject) return null;
    if (!activeProject) return null;
    return '/project-settings#xlsx-csv-mapping';
  }, [activeProject, activeProjectId, canManageProject]);

  return (
    <ConfirmDialog
      open={open}
      title="Upload Results Files"
      description="Add XML, JSON, XLSX, or CSV results files for processing."
      confirmLabel="Upload"
      confirmColor="primary"
      confirmDisabled={confirmDisabled}
      loading={loading}
      maxWidth="sm"
      gap="lg"
      onClose={onClose}
      onConfirm={onConfirm}
    >
      <Stack gap="lg" align="stretch">
        {selectedProjectId ? null : (
          <Stack gap="xs">
            <Text role="body" component="label" htmlFor={uploadProjectIdFieldId}>
              Project
            </Text>
            <Select
              required
              value={uploadProjectId}
              inputProps={{ id: uploadProjectIdFieldId, 'aria-label': 'Project' }}
              displayEmpty
              renderValue={(value) => {
                if (value !== '') {
                  const match = projectOptions.find((project) => project.id === value);
                  return match ? match.name : value;
                }
                return (
                  <Text role="body" color="textSecondary">
                    Select project
                  </Text>
                );
              }}
              onChange={(event) => {
                onUploadProjectIdChange(event.target.value);
              }}
            >
              {projectOptions.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        )}
        <Stack gap="xs">
          <Text role="body" component="label">
            Tags
          </Text>
          <TextField
            size="small"
            placeholder="smoke, e2e, api"
            value={uploadTagsInput}
            onChange={(event) => {
              onUploadTagsInputChange(event.target.value);
            }}
          />
        </Stack>
        <Box
          component="label"
          sx={{
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: resolveSpacing('sm'),
            px: resolveSpacing('lg'),
            py: resolveSpacing('lg'),
            minHeight: `calc(${tableTokens.rowHeight * 7}px)`,
            width: '100%',
            cursor: 'pointer',
            color: 'text.secondary',
            borderRadius: r('md'),
            border: (theme) => `3px dashed ${resolveSemanticColor(theme, 'accent')}`,
            backgroundColor: (theme) =>
              isDropActive
                ? resolveSemanticColor(theme, 'pageAltBg')
                : resolveSemanticColor(theme, 'pageBg'),
          }}
          onDragEnter={(event) => {
            event.preventDefault();
            setIsDropActive(true);
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setIsDropActive(true);
          }}
          onDragLeave={(event) => {
            event.preventDefault();
            setIsDropActive(false);
          }}
          onDrop={(event) => {
            event.preventDefault();
            setIsDropActive(false);
            handleFilesSelected(Array.from(event.dataTransfer.files));
          }}
        >
          <UploadFile fontSize="large" />
          {uploadFiles.length === 0 ? <Text role="body">No files selected</Text> : null}
          <Text role="body" color="textSecondary">
            {uploadFiles.length === 0 ? 'Click to add files' : 'Click to add more files'}
          </Text>
          <input
            type="file"
            hidden
            multiple
            accept=".xml,.json,.csv,.xlsx"
            onChange={(event) => {
              handleFilesSelected(Array.from(event.target.files ?? []));
              event.target.value = '';
            }}
          />
        </Box>
        {uploadFiles.length > 0 ? (
          <Stack gap="md" align="stretch">
            <List
              dense
              disablePadding
              sx={{
                borderRadius: r('card'),
                p: resolveSpacing('none'),
                px: resolveSpacing('none'),
                mx: `-${s('md')}`,
                width: `calc(100% + ${s('md')} + ${s('md')})`,
                '& .MuiListItem-root + .MuiListItem-root': {
                  mt: s('md'),
                },
              }}
            >
              {uploadFiles.map((file) => {
                const supported = isSupportedFile(file);
                const isCsv = isCsvResultsFile(file);
                const isXlsx = isSpreadsheetResultsFile(file);
                const isJson = isJsonResultsFile(file);
                const isXml = isXmlResultsFile(file);
                const isNonCsv = isJson || isXml;
                const csvFormatLabel = isXlsx ? 'XLSX' : 'CSV';
                const supportedFileTypeText = isXlsx
                  ? 'xlsx'
                  : isCsv
                    ? 'csv'
                    : isJson
                      ? 'json'
                      : isXml
                        ? 'xml'
                        : file.type || 'Unknown type';
                const csvHeaderStatus = isCsv
                  ? (csvHeaderStatusByName[file.name] ?? 'pending')
                  : undefined;
                const fileValidationStatus = isNonCsv
                  ? (fileValidationStatusByName[file.name] ?? 'pending')
                  : undefined;
                const validationStatus = isCsv ? csvHeaderStatus : fileValidationStatus;
                const showValidationChip =
                  validationStatus === 'valid' || validationStatus === 'invalid';
                const isValidationPending = validationStatus === 'pending' && (isCsv || isNonCsv);
                const isCsvInvalid = isCsv && csvHeaderStatus === 'invalid';
                const isFileInvalid = isNonCsv && fileValidationStatus === 'invalid';
                const invalidReason = isCsvInvalid
                  ? (csvHeaderReasonByName[file.name] ?? 'Unmatched headers: suite, test, status')
                  : isFileInvalid
                    ? (fileValidationReasonByName[file.name] ??
                      (isJson ? 'Invalid JSON.' : 'Invalid XML.'))
                    : null;
                const isChecked = selectedFiles.has(file.name);
                const secondaryText = isCsvInvalid
                  ? 'Required headers missing (will not be uploaded)'
                  : isFileInvalid
                    ? isJson
                      ? 'JSON is malformed (will not be uploaded)'
                      : 'XML is malformed (will not be uploaded)'
                    : supported
                      ? supportedFileTypeText
                      : 'Unsupported file type (will not be uploaded)';
                const secondaryColor =
                  supported && !isCsvInvalid && !isFileInvalid
                    ? 'textSecondary'
                    : isValidationPending
                      ? 'textSecondary'
                      : 'error';
                const showConfigureLink = isCsvInvalid && Boolean(configureCsvHref);
                const configureLink = showConfigureLink ? (
                  <Link
                    component={RouterLink}
                    to={configureCsvHref ?? ''}
                    target="_blank"
                    rel="noreferrer"
                    underline="hover"
                    color="textSecondary"
                    sx={{
                      whiteSpace: 'nowrap',
                      fontSize: typography.body.fontSize,
                      lineHeight: typography.body.lineHeight,
                    }}
                  >
                    {isXlsx ? 'Configure XLSX' : 'Configure CSV'}
                  </Link>
                ) : null;
                const validationChip = showValidationChip ? (
                  <Chip
                    label={validationStatus === 'valid' ? 'VALID' : 'INVALID'}
                    tone={validationStatus === 'valid' ? 'success' : 'error'}
                    size="xs"
                  />
                ) : null;
                const buildInvalidTooltip = (reason: string | null): ReactElement => {
                  if (!reason) {
                    return (
                      <Text role="body">
                        {csvFormatLabel} headers do not match required fields.
                      </Text>
                    );
                  }
                  const match = /missing required header(?:s)?:\s*(.+)/i.exec(reason);
                  if (!match) {
                    return (
                      <Text role="body" sx={{ whiteSpace: 'pre-line' }}>
                        {reason}
                      </Text>
                    );
                  }
                  const rawFields = match[1].replace(/\.+$/, '');
                  const fields = rawFields
                    .split(',')
                    .map((value) => value.trim().replace(/\.+$/, ''))
                    .filter((value) => value.length > 0);
                  const formatFieldLabel = (value: string): string => {
                    return value
                      .split(/[_\s-]+/)
                      .filter((part) => part.length > 0)
                      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
                      .join(' ');
                  };
                  const formattedFields = fields.map(formatFieldLabel);
                  return (
                    <Text role="body" sx={{ lineHeight: 1.3 }}>
                      Missing headers: {formattedFields.join(', ')}
                    </Text>
                  );
                };
                const invalidReasonIcon = isCsvInvalid ? (
                  <Tooltip title={buildInvalidTooltip(invalidReason ?? null)} placement="top">
                    <HelpOutline
                      fontSize="small"
                      sx={(theme) => ({ color: resolveSemanticColor(theme, 'textSecondary') })}
                    />
                  </Tooltip>
                ) : null;
                return (
                  <ListItem
                    key={file.name}
                    dense
                    sx={{
                      px: resolveSpacing('md'),
                      py: resolveSpacing('none'),
                    }}
                  >
                    <ListItemButton
                      sx={(theme) => ({
                        px: resolveSpacing('md'),
                        py: resolveSpacing('sm'),
                        width: '100%',
                        borderRadius: r('md'),
                        border: `1px solid ${resolveSemanticColor(theme, 'border')}`,
                        alignItems: 'center',
                      })}
                      onClick={() => {
                        if (!supported) return;
                        setSelectedFiles((current) => {
                          const next = new Set(current);
                          if (next.has(file.name)) {
                            next.delete(file.name);
                          } else {
                            next.add(file.name);
                          }
                          return next;
                        });
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: supported ? resolveSpacing('sm') : resolveSpacing('lg'),
                          ml: resolveSpacing('sm'),
                          alignSelf: 'center',
                        }}
                      >
                        {supported ? (
                          <Checkbox edge="start" checked={isChecked} disableRipple />
                        ) : isValidationPending ? (
                          <Checkbox edge="start" indeterminate disabled />
                        ) : (
                          <CloseIcon
                            fontSize="small"
                            sx={(theme) => ({
                              color: resolveSemanticColor(theme, 'error'),
                            })}
                          />
                        )}
                      </ListItemIcon>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: resolveSpacing('sm'),
                          width: '100%',
                          minWidth: 0,
                        }}
                      >
                        <Stack
                          gap="none"
                          align="stretch"
                          sx={{ minWidth: 0, py: resolveSpacing('xs') }}
                        >
                          <Text role="body" component="span" truncate sx={{ lineHeight: 1.2 }}>
                            {file.name}
                          </Text>
                          <Text
                            role="body"
                            color={secondaryColor}
                            component="span"
                            sx={{ lineHeight: 1.2, mt: '2px' }}
                          >
                            {secondaryText}
                          </Text>
                        </Stack>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: resolveSpacing('md') }}
                        >
                          {configureLink}
                          {validationChip}
                          {invalidReasonIcon}
                        </Box>
                      </Box>
                      <IconButton
                        size="medium"
                        aria-label={`Remove ${file.name}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          setUploadFiles((current) =>
                            current.filter((item) => item.name !== file.name),
                          );
                          setSelectedFiles((current) => {
                            if (!current.has(file.name)) return current;
                            const next = new Set(current);
                            next.delete(file.name);
                            return next;
                          });
                          setCsvHeaderStatusByName((current) => {
                            if (!current[file.name]) return current;
                            const { [file.name]: _removed, ...rest } = current;
                            return rest;
                          });
                          setCsvHeaderReasonByName((current) => {
                            if (!current[file.name]) return current;
                            const { [file.name]: _removed, ...rest } = current;
                            return rest;
                          });
                          setFileValidationStatusByName((current) => {
                            if (!current[file.name]) return current;
                            const { [file.name]: _removed, ...rest } = current;
                            return rest;
                          });
                          setFileValidationReasonByName((current) => {
                            if (!current[file.name]) return current;
                            const { [file.name]: _removed, ...rest } = current;
                            return rest;
                          });
                          setUploadError(null);
                        }}
                        sx={(theme) => ({
                          ml: invalidReasonIcon ? `-${s('xs')}` : resolveSpacing('none'),
                          mr: `-${s('md')}`,
                          alignSelf: 'center',
                          color: resolveSemanticColor(theme, 'textSecondary'),
                          borderRadius: '999px',
                        })}
                      >
                        <CloseIcon fontSize="medium" />
                      </IconButton>
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
            <Text role="body" color="textSecondary" align="right">
              {uploadFiles.length} file{uploadFiles.length === 1 ? '' : 's'} added
            </Text>
          </Stack>
        ) : null}
      </Stack>
      {uploadError ? <InlineError message={uploadError} /> : null}
    </ConfirmDialog>
  );
};
